import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { getPrerenderPath, localizedRoutes, stripHtmlSuffix, type RouteKey } from "@/lib/i18n/routes";
import { usePublicBlogPosts } from "@/hooks/useBlogPosts";

// Main navigation sequence (order of pages)
const NAVIGATION_SEQUENCE: (RouteKey | 'home')[] = [
  'home',
  'briefDescription',
  'features',
  'vacationRentalPms',
  'productChannelManager',
  'productWebsiteBuilder',
  'ai',
  'integrations',
  'forWhomSmallBusiness',
  'forWhomMediumLarge',
  'pricing',
  'compare',
  'testimonials',
  'blog',
  'faq',
  'about',
  'contact',
];

const LEGAL_PAGES: RouteKey[] = ['terms', 'privacy', 'publicationTerms', 'websiteTerms'];

const PageNavigationButtons = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const prevPathRef = useRef<string | null>(null);
  const currentPathRef = useRef(location.pathname);
  const skipTrackingRef = useRef(false);
  const scrollPositions = useRef<Record<string, number>>({});
  const restoreScrollRef = useRef<string | null>(null);

  const routes = localizedRoutes[language] || localizedRoutes.en;
  const normalizedPath = stripHtmlSuffix(location.pathname);

  // Detect if we're on a blog post page
  const blogSlugMatch = normalizedPath.match(/^(?:\/[a-z]{2})?\/blog\/(.+)$/);
  const isBlogPost = !!blogSlugMatch;
  const currentSlug = blogSlugMatch?.[1];

  // Only fetch blog posts when actually on a blog post page (huge payload otherwise)
  const { posts: blogPosts } = usePublicBlogPosts(language, isBlogPost);

  // Save scroll position continuously
  useEffect(() => {
    const saveScroll = () => {
      scrollPositions.current[currentPathRef.current] = window.scrollY;
    };
    window.addEventListener("scroll", saveScroll, { passive: true });
    return () => window.removeEventListener("scroll", saveScroll);
  }, []);

  // Track previous path and restore scroll when needed
  useEffect(() => {
    if (location.pathname !== currentPathRef.current) {
      if (skipTrackingRef.current) {
        skipTrackingRef.current = false;
      } else {
        prevPathRef.current = currentPathRef.current;
      }
      currentPathRef.current = location.pathname;
    }

    if (restoreScrollRef.current) {
      const path = restoreScrollRef.current;
      restoreScrollRef.current = null;
      const saved = scrollPositions.current[path];
      if (saved != null) {
        requestAnimationFrame(() => {
          setTimeout(() => window.scrollTo({ top: saved, left: 0, behavior: "instant" as ScrollBehavior }), 50);
        });
      }
    }
  }, [location.pathname]);


  // Find current route key from pathname
  const getCurrentRouteKey = (): RouteKey | 'home' | null => {
    const path = normalizedPath;

    if (path === '/' || path === `/${language}` || path === `/${language}/`) {
      return 'home';
    }

    for (const [key, route] of Object.entries(routes)) {
      if (route === path || route === path.replace(/\/$/, '')) {
        return key as RouteKey;
      }
    }

    return null;
  };

  const currentKey = getCurrentRouteKey();

  // Hide on admin pages, auth pages
  if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/auth")) return null;

  // If it's neither a known route nor a blog post, hide
  if (!currentKey && !isBlogPost) return null;

  const localePath = (key: RouteKey | 'home') => {
    if (key === 'home') return getPrerenderPath(routes.home);
    return getPrerenderPath(routes[key]);
  };

  const blogBasePath = getPrerenderPath(routes.blog || '/blog');

  const makeBlogPostPath = (slug: string) => {
    return `${blogBasePath}/${slug}`;
  };

  // Blog post navigation
  if (isBlogPost && currentSlug && blogPosts.length > 0) {
    const currentIndex = blogPosts.findIndex(p => p.slug === currentSlug);

    const handleBlogLeft = () => {
      if (currentIndex <= 0) {
        navigate(blogBasePath);
      } else {
        navigate(makeBlogPostPath(blogPosts[currentIndex - 1].slug));
      }
    };

    const handleBlogRight = () => {
      if (currentIndex >= blogPosts.length - 1 || currentIndex === -1) {
        navigate(blogBasePath);
      } else {
        navigate(makeBlogPostPath(blogPosts[currentIndex + 1].slug));
      }
    };

    const btnClass =
      "fixed bottom-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-110 hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2";

    return (
      <>
        <button
          onClick={handleBlogLeft}
          className={`${btnClass} left-6`}
          aria-label="Previous blog post"
        >
          <ChevronLeft size={24} strokeWidth={2.5} />
        </button>
        <button
          onClick={handleBlogRight}
          className={`${btnClass} right-6`}
          aria-label="Next blog post"
        >
          <ChevronRight size={24} strokeWidth={2.5} />
        </button>
      </>
    );
  }

  // If blog post but posts not loaded yet, show nothing temporarily
  if (isBlogPost) return null;

  // Standard page navigation
  if (!currentKey) return null;

  const menuIndex = NAVIGATION_SEQUENCE.indexOf(currentKey);
  const isInMenuSequence = menuIndex !== -1;
  const isLegalPage = LEGAL_PAGES.includes(currentKey as RouteKey);
  const isHome = currentKey === 'home';

  const showLeft = isInMenuSequence || isLegalPage;
  const showRight = isInMenuSequence && menuIndex < NAVIGATION_SEQUENCE.length - 1;

  if (!showLeft && !showRight) return null;

  const handleLeft = () => {
    if (isHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (prevPathRef.current && prevPathRef.current !== location.pathname) {
      const target = prevPathRef.current;
      prevPathRef.current = null;
      skipTrackingRef.current = true;
      restoreScrollRef.current = target;
      navigate(target);
      return;
    }

    skipTrackingRef.current = true;

    if (isInMenuSequence && menuIndex > 0) {
      const prevKey = NAVIGATION_SEQUENCE[menuIndex - 1];
      navigate(localePath(prevKey));
      return;
    }

    navigate(localePath('home'));
  };

  const handleRight = () => {
    if (isInMenuSequence && menuIndex < NAVIGATION_SEQUENCE.length - 1) {
      const nextKey = NAVIGATION_SEQUENCE[menuIndex + 1];
      navigate(localePath(nextKey));
    }
  };

  const btnClass =
    "fixed bottom-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-110 hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2";

  return (
    <>
      {showLeft && (
        <button
          onClick={handleLeft}
          className={`${btnClass} left-6`}
          aria-label="Previous page"
        >
          <ChevronLeft size={24} strokeWidth={2.5} />
        </button>
      )}
      {showRight && (
        <button
          onClick={handleRight}
          className={`${btnClass} right-6`}
          aria-label="Next page"
        >
          <ChevronRight size={24} strokeWidth={2.5} />
        </button>
      )}
    </>
  );
};

export default PageNavigationButtons;
